import React, { useEffect } from "react";
import { useDispatch, useSelector } from "../../../store";
import { fetchActiveSchedules } from "../../../slices/schedule";
import {
  Box,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  TableContainer,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { ExpandMore, MoreVert } from "@mui/icons-material";
import dayjs from "dayjs";
import Button from "../../base/Button";
import theme from "../../../theme";

interface Props {
  handleViewReport?: (id: number) => void;
  handleEditReport?: (id: number) => void;
  handleDisableReport?: (id: number) => void;
  handleEditGroup?: (id: number) => void;
  handleDeleteGroup?: (id: number) => void;
}

const ScheduledReportsStack: React.FC<Props> = (props) => {
  const {
    handleViewReport,
    handleEditReport,
    handleDisableReport,
    handleEditGroup,
    handleDeleteGroup,
  } = props;

  const dispatch = useDispatch();
  const { activeReports } = useSelector((store) => store.schedules);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedScheduleId, setSelectedScheduleId] = React.useState<
    number | null
  >(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    dispatch(fetchActiveSchedules());
  }, [dispatch]);

  return activeReports ? (
    <TableContainer
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "100%",
        gap: 1,
      }}
    >
      {activeReports.map((x) => {
        let tiles = x.tiles;
        let schedule = x.schedule;

        return (
          <Box key={x.schedule.id} display="flex" flexDirection="column">
            <Accordion
              sx={{
                display: "flex",
                flexDirection: "column",
                textAlign: "center",
              }}
              defaultExpanded={false}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  borderRadius: 1,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent={"center"}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setAnchorEl(e.currentTarget);
                      setSelectedScheduleId(schedule.id ?? null);
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  <Box
                    display="flex"
                    flexDirection={"row"}
                    alignItems="center"
                    width="100%"
                    gap={1}
                  >
                    <Typography flex={1} textAlign="center" variant="caption">
                      {schedule.name}
                    </Typography>
                    <Typography flex={1} textAlign="center" variant="caption">{`
                  ${
                    schedule.day?.value ??
                    (schedule.dayOfMonth
                      ? `Day ${schedule.dayOfMonth}`
                      : "Daily")
                  } 
                  at 
                  ${dayjs(schedule.time).format("hh:mm A")}
                  `}</Typography>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection={"row"}
                    alignItems="center"
                    width="100%"
                    gap={1}
                  >
                    <Typography flex={1} textAlign="center" variant="caption">
                      {schedule.emailGroups &&
                        schedule.emailGroups.map((e) => e.name).join(", ")}
                    </Typography>
                    <Typography flex={1} textAlign="center" variant="caption">
                      {tiles?.length! > 0
                        ? `${tiles?.length} Tile${tiles?.length! > 1 ? "s" : ""}`
                        : "No tiles added"}
                    </Typography>
                  </Box>
                  <Menu
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    anchorEl={anchorEl}
                    open={open && selectedScheduleId === schedule.id}
                    onClose={() => {
                      setAnchorEl(null);
                      setSelectedScheduleId(null);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        setSelectedScheduleId(null);
                        handleEditGroup?.(schedule.id!);
                      }}
                    >
                      Edit Schedule
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        setSelectedScheduleId(null);
                        handleDeleteGroup?.(schedule.id!);
                      }}
                    >
                      Delete Schedule
                    </MenuItem>
                  </Menu>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {tiles?.length! > 0 ? (
                  tiles.map((tile, index) => (
                    <Box
                      key={tile.id}
                      display="flex"
                      flexDirection={"row"}
                      alignItems="center"
                      width="100%"
                      gap={1}
                      paddingY={1}
                      borderBottom={
                        index !== tiles.length - 1 ? "1px solid gray" : "none"
                      }
                    >
                      <Box
                        display={"flex"}
                        width="100%"
                        flexDirection={"column"}
                        gap={1}
                      >
                        <Typography
                          flex={1}
                          textAlign="center"
                          variant="caption"
                        >
                          {tile.dashboard?.value
                            ? `${tile.dashboard.value} - ${tile.title}`
                            : ""}
                        </Typography>
                        <Typography
                          flex={1}
                          textAlign="center"
                          variant="caption"
                        >
                          {tile.dataType?.value}
                        </Typography>
                      </Box>
                      <Box
                        display={"flex"}
                        width="100%"
                        flexDirection={"column"}
                      >
                        <Button
                          color="success"
                          onClick={() => handleViewReport!(tile.id!)}
                          label="View"
                        />
                        <Button
                          color="warning"
                          onClick={() => handleEditReport!(tile.id!)}
                          label="Edit"
                        />
                        <Button
                          color="error"
                          onClick={() => handleDisableReport!(tile.id!)}
                          label="Remove"
                        />
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="caption">
                    Tiles added to this schedule will be displayed here.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      })}
    </TableContainer>
  ) : (
    <></>
  );
};

export default ScheduledReportsStack;
